import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ProfileModal({ open, onClose }) {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const updated = await api.put("/auth/profile", { name, email });
      setUser((prev) => ({ ...prev, ...updated.data }));
      toast.success("Profil mis à jour");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    setSavingPwd(true);
    try {
      await api.put("/auth/change-password", {
        current_password: currentPwd,
        new_password: newPwd,
      });
      toast.success("Mot de passe modifié");
      setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mon profil</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="profile">
          <TabsList className="w-full">
            <TabsTrigger value="profile" className="flex-1">Informations</TabsTrigger>
            <TabsTrigger value="password" className="flex-1">Mot de passe</TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <form onSubmit={handleSaveProfile} className="mt-4 space-y-4">
              <div className="space-y-1">
                <Label>Nom</Label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre nom" />
              </div>
              <div className="space-y-1">
                <Label>Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={savingProfile}>
                  {savingProfile && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Enregistrer
                </Button>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="password">
            <form onSubmit={handleChangePassword} className="mt-4 space-y-4">
              <div className="space-y-1">
                <Label>Mot de passe actuel</Label>
                <Input type="password" value={currentPwd}
                  onChange={(e) => setCurrentPwd(e.target.value)} required />
              </div>
              <div className="space-y-1">
                <Label>Nouveau mot de passe</Label>
                <Input type="password" value={newPwd}
                  onChange={(e) => setNewPwd(e.target.value)} required minLength={6} />
              </div>
              <div className="space-y-1">
                <Label>Confirmer le nouveau mot de passe</Label>
                <Input type="password" value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.target.value)} required />
              </div>
              <div className="flex justify-end">
                <Button type="submit" disabled={savingPwd}>
                  {savingPwd && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Changer
                </Button>
              </div>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
